import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  CurrencyCircleDollar,
  Percent,
  Prohibit,
  UserPlus,
  UsersThree,
} from "@phosphor-icons/react";
import Metric from "../components/Metric";
import api from "../lib/api";

const money = (value) => {
  return new Intl.NumberFormat("en-MY", {
    style: "currency",
    currency: "MYR",
    maximumFractionDigits: 0,
  }).format(value || 0);
};

export default function DashboardPage({ user }) {
  const [data, setData] = useState(null);

  const loadDashboard = useCallback(async () => {
    const response = await api.get("/dashboard");
    setData(response.data);
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (!data) {
    return (
      <div className="page-loading" data-testid="dashboard-loading">
        Loading operations view…
      </div>
    );
  }

  const collectedTotal = data.interest_collected ?? data.collected_total;
  const interestCollectedThisMonth = data.interest_collected_this_month ?? 0;
  const isOfficer = user.role === "officer";
  const isCfCc = user.user_id === "CF-CC";
  const showPersonalSales = user.role === "master";
  const showTeamSales = !isCfCc && (
    user.role === "master" || (isOfficer && data.team_members > 1)
  );
  const hasPersonalAndTeamView = showPersonalSales || showTeamSales;

  return (
    <div className="page loanpro-dashboard" data-testid="dashboard-page">
      <header className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p>
            {hasPersonalAndTeamView
              ? "Your personal sales and downline performance"
              : isOfficer
                ? "Overview of your personal loan portfolio"
                : "Overview of the company loan portfolio"}
          </p>
        </div>
      </header>
      <div className="metric-grid loanpro-metrics">
        {isCfCc && (
          <>
            <Metric
              Icon={UsersThree}
              label="Team On Going Sales"
              value={money(data.team_ongoing_sales)}
              footerLabel="CF-CC + downline balance"
              footerValue={money(data.team_ongoing_sales)}
              tone="green"
              testId="cf-cc-team-ongoing-sales-metric"
            />
            <Metric
              Icon={CurrencyCircleDollar}
              label="On Going Sales"
              value={money(data.active_outstanding)}
              footerLabel="CF-CC personal balance"
              footerValue={money(data.active_outstanding)}
              tone="blue"
              testId="cf-cc-ongoing-sales-metric"
            />
            <Metric
              Icon={UsersThree}
              label="Team Sales"
              value={money(data.team_sales)}
              footerLabel="Team Interest Collected This Month"
              footerValue={money(data.team_interest_collected_this_month)}
              tone="green"
              testId="cf-cc-team-sales-metric"
            />
          </>
        )}
        {showPersonalSales && (
          <>
            <Metric
              Icon={CurrencyCircleDollar}
              label="Personal sales"
              value={money(data.personal_sales)}
              footerLabel="Personal collections"
              footerValue={money(data.personal_collections)}
              tone="blue"
              testId="personal-sales-metric"
            />
          </>
        )}
        {showTeamSales && (
          <>
            <Metric
              Icon={UsersThree}
              label="Team sales"
              value={money(data.team_sales)}
              footerLabel="Team collections"
              footerValue={money(data.team_collections)}
              tone="green"
              testId="team-sales-metric"
            />
          </>
        )}
        {!isCfCc && (
          <Metric
            Icon={CurrencyCircleDollar}
            label={isOfficer ? "On Going Sales" : "On going loan"}
            value={money(data.active_outstanding)}
            footerLabel="New Loan This Month"
            footerValue={money(data.new_loan_amount_this_month)}
            tone="blue"
            testId="ongoing-loan-metric"
          />
        )}
        <Metric
          Icon={Percent}
          label="Total interest collected"
          value={money(collectedTotal)}
          footerLabel="Interest Collected This Month"
          footerValue={money(interestCollectedThisMonth)}
          tone="ink"
          testId="interest-collected-metric"
        />
        <Metric
          Icon={UsersThree}
          label="Total customers"
          value={data.total_customers}
          footerLabel="New This Month"
          footerValue={data.new_loans_this_month}
          tone="green"
          testId="total-customers-metric"
        />
        <Metric
          Icon={UserPlus}
          label="New customer loan"
          value={data.new_loans_this_month}
          footerLabel="Existing Customer Loan"
          footerValue={data.existing_customer_loans}
          tone="amber"
          testId="new-customer-loan-metric"
        />
        <Metric
          Icon={Prohibit}
          label="Total blacklist amount"
          value={money(data.blacklist_amount)}
          footerLabel="Blacklist This Month"
          footerValue={money(data.blacklist_amount)}
          tone="red"
          testId="blacklist-amount-metric"
        />
        {!isOfficer && (
          <Metric
            Icon={Briefcase}
            label="Managers + agents"
            value={data.team_members}
            footerLabel="New This Month"
            footerValue={data.new_loans_this_month}
            tone="ink"
            testId="managers-agents-metric"
          />
        )}
      </div>
      <section className="loanpro-panel loanpro-recent-loans" data-testid="recent-loans-panel">
        <div className="loanpro-panel-heading">
          <h2>Recent Loans</h2>
          <Link to="/loans" data-testid="view-recent-loans-link">
            View all <ArrowRight size={13} />
          </Link>
        </div>
        <div className="loanpro-panel-list">
          {data.recent_loans.length ? (
            data.recent_loans.map((loan) => (
              <div
                className="loanpro-row"
                key={`${loan.borrower_name}-${loan.created_at}`}
                data-testid={`dashboard-loan-${loan.borrower_name}`}
              >
                <div>
                  <strong>{loan.borrower_name}</strong>
                  <span>
                    {money(loan.principal)} · {loan.product_name}
                  </span>
                </div>
                <span className={`loanpro-status loanpro-status-${loan.status}`}>
                  {loan.status === "current" ? "Active" : loan.status}
                </span>
              </div>
            ))
          ) : (
            <p data-testid="recent-loans-empty">No loans yet.</p>
          )}
        </div>
      </section>
    </div>
  );
}